import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from 'providers/AppProvider';
import CarManagmentProvider, {
  useCarManagmentContext,
  specificCarTrips
} from 'providers/CarManagmentProvider';
import { Spinner, Card, Row, Col, Button, Modal } from 'react-bootstrap';
import { QuickTripModal } from 'components/modals/QuickTripModal';
import { ObservationModal } from 'components/modals/ObservationModal';
import dayjs from 'dayjs';

const index = () => {
  return (
    <CarManagmentProvider>
      <TripManagement />
    </CarManagmentProvider>
  );
};

const TripManagement = () => {
  const { isAuthenticated, getCurrentUser } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { guidCarro } = useParams<{ guidCarro: string }>();

  const currentUser = getCurrentUser();

  const {
    // isLoadingSpecificCarTrips,
    specificCarTrips,
    trips,
    getSpecificCarTrips,
    startTrip,
    endTrip,
    recordDriverChange,
    carsTrips,
    refreshTrips
  } = useCarManagmentContext();

  const [showQuickTripModal, setShowQuickTripModal] = useState(false);
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [selectedTripForObservation, setSelectedTripForObservation] =
    useState<number>(0);
  const [pendingStartTripId, setPendingStartTripId] = useState<number | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(true);

  // Add selected base trip state
  const [selectedBaseTrip, setSelectedBaseTrip] =
    useState<specificCarTrips | null>(null);

  const selectedGroupId = (
    location.state as { selectedGroupId?: number } | null
  )?.selectedGroupId;

  // Check if current user is a participant in a trip
  const isUserParticipant = (trip: { usersList?: string }): boolean => {
    if (!currentUser || !trip.usersList) return false;

    const names = trip.usersList.split(',').map(n => n.trim());

    return names.includes(currentUser.name);
  };

  const isCurrentUserDriver = (trip: specificCarTrips): boolean => {
    if (!currentUser?.name || !trip.driverName) return false;

    return (
      trip.driverName.trim().toLowerCase() ===
      currentUser.name.trim().toLowerCase()
    );
  };

  const shouldShowDriverButton = (trip: specificCarTrips): boolean => {
    return (
      isUserParticipant(trip) &&
      isTripStarted(trip) &&
      !isCurrentUserDriver(trip)
    );
  };

  const shouldShowFinishTripButton = (trip: specificCarTrips): boolean => {
    return (
      isUserParticipant(trip) &&
      isTripStarted(trip) &&
      isCurrentUserDriver(trip)
    );
  };

  // Check if trip has been started (start_registered is not null)
  const isTripStarted = (trip: specificCarTrips): boolean => {
    return (
      trip.start_registered !== null && trip.start_registered !== undefined
    );
  };

  // Check if trip has been ended (end_registered is not null)
  const isTripEnded = (trip: specificCarTrips): boolean => {
    return trip.end_registered !== null && trip.end_registered !== undefined;
  };

  // Check if trip is pending and should count for future scheduling
  const isTripNotStartedOrEnded = (trip: specificCarTrips): boolean => {
    return !isTripStarted(trip) && !isTripEnded(trip);
  };

  // Check if trip should be underway based on scheduled time
  const shouldBeUnderway = (trip: specificCarTrips): boolean => {
    const now = dayjs();
    const start = dayjs(trip.start);
    const end = trip.end_registered
      ? dayjs(trip.end_registered)
      : trip.end
        ? dayjs(trip.end)
        : null;
    return start.isBefore(now) && (!end || end.isAfter(now));
  };

  // Check if trip is in the near future (starting within 10 minutes)
  const isTripNearFuture = (
    trip: specificCarTrips,
    minutes: number = 10
  ): boolean => {
    const now = dayjs();
    const start = dayjs(trip.start);
    const threshold = now.add(minutes, 'minute');
    return start.isAfter(now) && start.isBefore(threshold);
  };

  const groupTripsByOverlap = (trips: specificCarTrips[]) => {
    const tripsByGroupId = new Map<number, specificCarTrips[]>();
    trips.forEach(trip => {
      const groupTrips = tripsByGroupId.get(trip.groupID) || [];
      groupTrips.push(trip);
      tripsByGroupId.set(trip.groupID, groupTrips);
    });

    const intervals = Array.from(tripsByGroupId, ([groupID, groupTrips]) => {
      const start = groupTrips.reduce(
        (earliest, trip) =>
          dayjs(trip.start).isBefore(earliest) ? dayjs(trip.start) : earliest,
        dayjs(groupTrips[0].start)
      );
      const end = groupTrips.reduce(
        (latest, trip) =>
          dayjs(trip.end).isAfter(latest) ? dayjs(trip.end) : latest,
        dayjs(groupTrips[0].end)
      );

      return {
        groupID,
        trips: groupTrips,
        start: start.toDate(),
        end: end.isAfter(start) ? end.toDate() : start.add(1, 'hour').toDate()
      };
    }).sort(
      (a, b) =>
        a.start.getTime() - b.start.getTime() ||
        a.end.getTime() - b.end.getTime()
    );

    const groups: {
      key: number;
      trips: specificCarTrips[];
      start: Date;
      end: Date;
    }[] = [];

    intervals.forEach(interval => {
      const currentGroup = groups[groups.length - 1];
      if (currentGroup && interval.start < currentGroup.end) {
        currentGroup.trips.push(...interval.trips);
        if (interval.end > currentGroup.end) currentGroup.end = interval.end;
        return;
      }

      groups.push({
        key: interval.groupID,
        trips: [...interval.trips],
        start: interval.start,
        end: interval.end
      });
    });

    return groups;
  };

  const getGroupPassengers = (groupTrips: specificCarTrips[]): string[] =>
    Array.from(
      new Set(
        groupTrips
          .flatMap(trip => [trip.name, ...(trip.usersList || '').split(',')])
          .map(name => name.trim())
          .filter(name => name && name !== 'N/A')
      )
    );

  const getGroupLocations = (groupTrips: specificCarTrips[]): string[] =>
    Array.from(
      new Set(
        groupTrips
          .map(trip => trip.locais?.trim())
          .filter((location): location is string => Boolean(location))
      )
    );

  // Return the largest duration option that fits before the next trip.
  const calculateMaxQuickTripDuration = (
    nextTrip: specificCarTrips
  ): number => {
    const now = dayjs();
    const nextStart = dayjs(nextTrip.start);
    const availableMinutes = nextStart.diff(now, 'minute') - 5;

    // If there is not enough time for a minimum 15-minute quick trip,
    // return 0 so the quick-trip option can be hidden/disabled.
    if (availableMinutes < 15) {
      return 0;
    }

    if (availableMinutes >= 60) {
      return 60;
    }

    if (availableMinutes >= 30) {
      return 30;
    }

    return 15;
  };

  // Categorize trips
  const categorizedTrips = useMemo(() => {
    if (
      !specificCarTrips ||
      specificCarTrips.some(item => 'idviagem' in item === false)
    ) {
      // setCarplate(specificCarTrips[0]?.carPlate || null);
      return { nearFuture: [], underway: [], future: [], ended: [] };
    }

    // const now = dayjs();
    return {
      // Trips starting within 10 minutes (highlighted but NOT auto-selected)
      nearFuture: specificCarTrips.filter(
        trip => isTripNotStartedOrEnded(trip) && isTripNearFuture(trip, 10)
      ),
      // Trips that are actually underway
      underway: specificCarTrips.filter(
        trip => isTripStarted(trip) && !isTripEnded(trip)
      ),
      // Future trips (>10 min away)
      future: specificCarTrips.filter(
        trip => isTripNotStartedOrEnded(trip) && !isTripNearFuture(trip, 10)
      ),
      // Ended trips
      ended: specificCarTrips.filter(trip => isTripEnded(trip))
    };
  }, [specificCarTrips]);

  // Combined list of trips to display (nearFuture + future)
  const displayableTrips = useMemo(() => {
    return [...categorizedTrips.nearFuture, ...categorizedTrips.future].sort(
      (a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
    );
  }, [categorizedTrips, specificCarTrips]);
  const displayableTripGroups = useMemo(
    () => groupTripsByOverlap(displayableTrips),
    [displayableTrips]
  );
  const underwayTripGroups = useMemo(
    () => groupTripsByOverlap(categorizedTrips.underway),
    [categorizedTrips.underway]
  );

  const nextFutureTrip = displayableTrips.find(trip =>
    dayjs(trip.start).isAfter(dayjs())
  );

  // Check if quick trip is allowed
  const canShowQuickTrip = useMemo(() => {
    const maxQuickDuration = nextFutureTrip
      ? calculateMaxQuickTripDuration(nextFutureTrip)
      : 60;

    return (
      categorizedTrips.underway.length === 0 &&
      categorizedTrips.nearFuture.length === 0 &&
      Boolean(guidCarro) &&
      !specificCarTrips.some(trip => shouldBeUnderway(trip)) &&
      specificCarTrips
        .filter(trip => isUserParticipant(trip))
        .every(trip => !shouldBeUnderway(trip)) &&
      selectedBaseTrip === null &&
      maxQuickDuration >= 15
    );
  }, [categorizedTrips, specificCarTrips, selectedBaseTrip, nextFutureTrip]);

  // Check if a trip is near future for styling
  const isNearFutureTrip = (trip: specificCarTrips): boolean => {
    return categorizedTrips.nearFuture.some(t => t.idviagem === trip.idviagem);
  };

  const showSelectedTripCard = Boolean(
    selectedBaseTrip && isUserParticipant(selectedBaseTrip)
  );
  const showUnderwayTripCard = Boolean(
    underwayTripGroups.length > 0 && !showSelectedTripCard
  );
  const showTripListCard = Boolean(
    displayableTrips.length > 0 && !showSelectedTripCard
  );

  const activeTripGroup =
    showSelectedTripCard && selectedBaseTrip
      ? groupTripsByOverlap(specificCarTrips).find(group =>
          group.trips.some(trip => trip.groupID === selectedBaseTrip.groupID)
        )
      : showUnderwayTripCard
        ? groupTripsByOverlap(specificCarTrips).find(group =>
            group.trips.some(
              trip => trip.groupID === categorizedTrips.underway[0].groupID
            )
          )
        : undefined;
  const activeGroupIds = new Set(
    activeTripGroup?.trips.map(trip => trip.groupID) || []
  );
  const otherTripGroups = displayableTripGroups.filter(
    group => !group.trips.some(trip => activeGroupIds.has(trip.groupID))
  );

  // Update handleQuickTrip function
  const handleQuickTrip = () => {
    setShowQuickTripModal(true);
  };

  // Handler for marking observations
  const markObservations = (trips: specificCarTrips[]) => {
    // Filter trips to only include those where user is a participant
    const participantsTrips = trips.filter(trip => isUserParticipant(trip));
    if (participantsTrips.length === 0) {
      return;
    }
    setSelectedTripForObservation(participantsTrips[0].groupID);
    setShowObservationModal(true);
  };

  // Handler for starting a trip
  const completeStartTrip = async (tripId: number) => {
    if (!guidCarro) return;

    try {
      const trip = specificCarTrips.find(t => t.idviagem === tripId);
      if (!trip || !isUserParticipant(trip)) {
        return;
      }

      const unfinishedTrips = specificCarTrips.filter(
        otherTrip =>
          otherTrip.groupID !== trip.groupID &&
          isTripStarted(otherTrip) &&
          !isTripEnded(otherTrip) &&
          specificCarTrips.findIndex(
            candidate =>
              candidate.groupID === otherTrip.groupID &&
              isTripStarted(candidate) &&
              !isTripEnded(candidate)
          ) === specificCarTrips.indexOf(otherTrip)
      );

      for (const unfinishedTrip of unfinishedTrips) {
        const endResult = await endTrip(unfinishedTrip.groupID);
        if (!endResult.success) {
          console.error(
            `Failed to automatically end trip ${unfinishedTrip.groupID}:`,
            endResult.error
          );
          return;
        }
      }

      // Start the trip
      const startResult = await startTrip(tripId);

      if (startResult.success) {
        await getSpecificCarTrips(guidCarro);
        setSelectedBaseTrip(null);
      } else {
        console.error('Failed to start trip:', startResult.error);
      }
    } catch (error) {
      console.error('Error starting trip:', error);
    } finally {
      setPendingStartTripId(null);
    }
  };

  const handleStartTrip = async (tripId: number) => {
    const trip = specificCarTrips.find(t => t.idviagem === tripId);
    if (!trip || !isUserParticipant(trip)) return;

    const hasUnfinishedTrip = specificCarTrips.some(
      otherTrip =>
        otherTrip.groupID !== trip.groupID &&
        isTripStarted(otherTrip) &&
        !isTripEnded(otherTrip)
    );

    if (hasUnfinishedTrip) {
      setPendingStartTripId(tripId);
      return;
    }

    await completeStartTrip(tripId);
  };

  // Handler for ending a trip
  const handleEndTrip = async (tripId: number) => {
    if (!guidCarro) return;

    // setIsEndingTrip(true);
    try {
      // Verify user is a participant in this trip
      const trip = specificCarTrips.find(t => t.idviagem === tripId);
      if (!trip || !isUserParticipant(trip)) {
        return;
      }

      const result = await endTrip(tripId);
      if (result.success) {
        // Trip ended successfully, refresh data
        await getSpecificCarTrips(guidCarro);
        setSelectedBaseTrip(null);
      } else {
        console.error('Failed to end trip:', result.error);
      }
    } catch (error) {
      console.error('Error ending trip:', error);
    }
    // finally {
    //   // setIsEndingTrip(false);
    // }
  };

  const handleSetDriver = async (tripId: number) => {
    if (!guidCarro || !currentUser) return;

    try {
      const result = await recordDriverChange(tripId);
      if (result.success) {
        await getSpecificCarTrips(guidCarro);
      } else {
        console.error('Failed to set driver:', result.error);
      }
    } catch (error) {
      console.error('Error setting driver:', error);
    }
  };

  // Check authentication and fetch car data
  useEffect(() => {
    const checkAuthAndCar = async () => {
      const authenticated = await isAuthenticated();
      if (!authenticated) {
        // This should not happen due to ProtectedRoute, but safe to check
        // navigate('/');
        setIsLoading(false);
        return;
      }

      if (!guidCarro) {
        setIsLoading(false);
        return;
      }

      try {
        await getSpecificCarTrips(guidCarro);
      } catch (err) {
        console.error('Failed to fetch car:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndCar();
  }, [guidCarro, isAuthenticated, navigate]);

  useEffect(() => {
    if (!selectedGroupId || specificCarTrips.length === 0) return;

    const selectedTrip = specificCarTrips.find(
      trip =>
        trip.groupID === selectedGroupId || trip.idviagem === selectedGroupId
    );

    if (selectedTrip) {
      setSelectedBaseTrip(selectedTrip);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [selectedGroupId, specificCarTrips, navigate, location.pathname]);

  // Format time only (HH:mm)
  const formatTime = (date: Date | string) => {
    return dayjs(date).format('HH:mm');
  };

  if (isLoading /*|| isLoadingSpecificCarTrips*/) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  if (specificCarTrips.length <= 0) {
    const now = dayjs();
    const nextHour = now.add(1, 'hour');
    const availableCars = carsTrips.filter(
      car =>
        !trips.some(trip => {
          if (trip.carPlate !== car.carPlate) return false;

          const tripStart = dayjs(trip.start);
          return (
            tripStart.isSame(now, 'day') ||
            (tripStart.isAfter(now) && tripStart.isBefore(nextHour))
          );
        })
    );
    // const currentCar = carsTrips.find(car => car.guidCarro === guidCarro);

    const userTripsBeforeCar = trips
      .filter(
        trip =>
          isUserParticipant(trip) &&
          dayjs(trip.start).isSame(dayjs(), 'day') &&
          trip.carPlate &&
          carsTrips.some(car => car.carPlate === trip.carPlate)
      )
      .filter(
        (trip, index, matchingTrips) =>
          matchingTrips.findIndex(
            candidate =>
              candidate.grupo === trip.grupo &&
              candidate.carPlate === trip.carPlate
          ) === index
      );

    return (
      <div className="container-trip-management">
        {userTripsBeforeCar.length > 0 && (
          <Card className="card-container-trip-management mb-3">
            <Card.Header className="card-container-header">
              <h5 className="card-container-title-today">
                As minhas viagens - {dayjs().format('DD/MM/YYYY')}
              </h5>
            </Card.Header>
            <Card.Body>
              <Row xs={1} md={2} lg={3} className="g-3">
                {userTripsBeforeCar.map(trip => {
                  const car = carsTrips.find(
                    item => item.carPlate === trip.carPlate
                  );
                  const hasTripPassed = Boolean(trip.end_registered);

                  return (
                    <Col key={trip.id}>
                      <Card
                        className={
                          hasTripPassed
                            ? 'card-container-trip-management-item card-container-trip-management-item-disabled'
                            : 'card-container-trip-management-item'
                        }
                        onClick={() => {
                          if (car && !hasTripPassed) {
                            navigate(`/viagens/${car.guidCarro}`, {
                              state: { selectedGroupId: trip.grupo }
                            });
                          }
                        }}
                      >
                        <Card.Body className="p-3">
                          <Card.Title className="px-1">
                            Destino: {trip.title || 'Desconhecido'}
                          </Card.Title>
                          <Card.Text className="px-1">
                            <strong>Passageiros:</strong>{' '}
                            {trip.usersList || 'N/A'}
                            <br />
                            <strong>Horário:</strong> {formatTime(trip.start)} -{' '}
                            {trip.end ? formatTime(trip.end) : 'N/A'}
                            <br />
                            <strong>Carro:</strong> {trip.carPlate || 'N/A'}
                          </Card.Text>
                          {!hasTripPassed && isUserParticipant(trip) && (
                            <Button
                              variant="primary"
                              className="w-100"
                              disabled={!car}
                              onClick={event => {
                                event.stopPropagation();
                                if (car && !hasTripPassed) {
                                  navigate(`/viagens/${car.guidCarro}`, {
                                    state: { selectedGroupId: trip.grupo }
                                  });
                                }
                              }}
                            >
                              Selecionar viagem
                            </Button>
                          )}
                          {!hasTripPassed && !isUserParticipant(trip) && (
                            <div
                              className="btn btn-primary w-100 invisible"
                              aria-hidden="true"
                            >
                              Selecionar viagem
                            </div>
                          )}
                          {hasTripPassed && (
                            <div
                              className="btn btn-primary w-100 invisible"
                              aria-hidden="true"
                            />
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        )}

        <Card className="card-container-trip-management">
          <Card.Header className="card-container-header">
            <h5 className="card-container-title-today">Carros disponíveis</h5>
          </Card.Header>
          <Card.Body>
            <Row xs={1} md={6} className="g-3">
              {availableCars.map(car => (
                <Col key={car.guidCarro}>
                  <Card
                    onClick={() => navigate(`/viagens/${car.guidCarro}`)}
                    className="card-container-trip-management-car shadow-sm align-items-center"
                  >
                    <Card.Body className="p-3">
                      <div className="fw-bold">{car.carPlate}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {/* {currentCar && (
          <Card>
            <Card.Body className="card-container-trip-management-quicktrip">
              <h5 className="card-container-title">Saída rápida</h5>
              <p className="card-text">
                Este carro não tem viagens hoje. Iniciar uma saída rápida?
              </p>
              <Button variant="primary" onClick={handleQuickTrip}>
                Iniciar saída rápida
              </Button>
            </Card.Body>
            <QuickTripModal
              show={showQuickTripModal}
              onHide={() => setShowQuickTripModal(false)}
              guidCarro={guidCarro || ''}
              matricula={currentCar?.carPlate || ''}
              maxDuration={60}
              onSuccess={async () => {
                await getSpecificCarTrips(guidCarro || '');
              }}
            />
          </Card>
        )} */}
      </div>
    );
  }

  return (
    <div className="container-trip-management">
      <div className="trip-management-header d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Gestão da viagem</h2>
        {/* {!selectedBaseTrip && (
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/viagens/procurar')}
          >
            Voltar
          </Button>
        )} */}
        <Button
          variant="outline-secondary"
          onClick={() => {
            setSelectedBaseTrip(null);
            refreshTrips();
            navigate('/viagens/procurar');
          }}
        >
          Voltar
        </Button>
      </div>
      <h3 className="subtitle-trip-management">
        Carro: {specificCarTrips[0]?.carPlate}
      </h3>

      {/* ========== SELECTED BASE TRIP (shown without card) ========== */}
      {showSelectedTripCard &&
        selectedBaseTrip &&
        (() => {
          const selectedGroupTrips = activeTripGroup?.trips || [
            selectedBaseTrip
          ];
          const selectedPassengers = getGroupPassengers(selectedGroupTrips);
          const selectedLocations = getGroupLocations(selectedGroupTrips);

          return (
            <Card className="card-container-trip-management mb-3">
              <Card.Body>
                <Card.Title className="card-container-title">
                  Passageiros: {selectedPassengers.join(', ') || 'Trip'}
                </Card.Title>
                <Card.Text>
                  <strong>Horário previsto: </strong>
                  {formatTime(
                    activeTripGroup?.start || selectedBaseTrip.start
                  )}{' '}
                  - {formatTime(activeTripGroup?.end || selectedBaseTrip.end)}
                  <br />
                  <strong>Destino:</strong>{' '}
                  {selectedLocations.join(', ') || 'N/A'}
                </Card.Text>

                <div className="button-container-trip-management">
                  {!isTripStarted(selectedBaseTrip) ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleStartTrip(selectedBaseTrip.groupID)}
                      disabled={!isUserParticipant(selectedBaseTrip)}
                    >
                      Iniciar viagem
                    </Button>
                  ) : !isTripEnded(selectedBaseTrip) ? (
                    <div className="trip-management-actions d-flex justify-content-end gap-2 mt-6">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => markObservations(selectedGroupTrips)}
                        disabled={!isUserParticipant(selectedBaseTrip)}
                      >
                        Marcar Observações
                      </Button>
                      {shouldShowDriverButton(selectedBaseTrip) && (
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() =>
                            handleSetDriver(selectedBaseTrip.groupID)
                          }
                          disabled={
                            !isUserParticipant(selectedBaseTrip) ||
                            !isTripStarted(selectedBaseTrip)
                          }
                        >
                          Definir-se como condutor
                        </Button>
                      )}
                      {shouldShowFinishTripButton(selectedBaseTrip) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            handleEndTrip(selectedBaseTrip.groupID)
                          }
                          disabled={!isUserParticipant(selectedBaseTrip)}
                        >
                          Finalizar Viagem
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => markObservations(selectedGroupTrips)}
                      disabled={!isUserParticipant(selectedBaseTrip)}
                    >
                      Marcar Observações
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          );
        })()}

      {/* ========== UNDERWAY TRIP (auto-selected) ========== */}
      {showUnderwayTripCard && (
        <>
          {underwayTripGroups.map(group => {
            const participantTrip = group.trips.find(isUserParticipant);
            const actionTrip = participantTrip || group.trips[0];
            const passengers = getGroupPassengers(group.trips);
            const locations = getGroupLocations(group.trips);
            const drivers = Array.from(
              new Set(
                group.trips
                  .map(trip => trip.driverName?.trim())
                  .filter((driver): driver is string => Boolean(driver))
              )
            );

            return (
              <Card
                className="card-container-trip-management mb-3"
                key={group.key}
              >
                <Card.Body>
                  <Card.Title className="card-container-title text-primary">
                    Viagem em curso
                  </Card.Title>
                  <Card.Text>
                    <strong>Horário:</strong> {formatTime(group.start)} -{' '}
                    {formatTime(group.end)}
                    <br />
                    <strong>Passageiros:</strong>{' '}
                    {passengers.join(', ') || 'N/A'}
                    <br />
                    <strong>Destino:</strong> {locations.join(', ') || 'N/A'}
                    <br />
                    <strong>Condutor:</strong> {drivers.join(', ') || 'N/A'}
                  </Card.Text>

                  <div className="button-container-trip-management">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => markObservations(group.trips)}
                      disabled={!participantTrip}
                    >
                      Marcar Observações
                    </Button>
                    {shouldShowDriverButton(actionTrip) && (
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => handleSetDriver(actionTrip.groupID)}
                        disabled={!isTripStarted(actionTrip)}
                      >
                        Definir-se como condutor
                      </Button>
                    )}
                    {shouldShowFinishTripButton(actionTrip) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleEndTrip(actionTrip.groupID)}
                      >
                        Finalizar Viagem
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </>
      )}

      {showSelectedTripCard && otherTripGroups.length > 0 && (
        <Card className="card-container-trip-management mb-3">
          <Card.Header className="card-container-header">
            <h5 className="mb-0">Outras viagens de hoje</h5>
          </Card.Header>
          <Card.Body>
            <Row xs={1} md={2} lg={3} className="g-3">
              {otherTripGroups.map(group => {
                const firstTrip = group.trips[0];
                const goingUserNames = getGroupPassengers(group.trips);
                const locations = getGroupLocations(group.trips);

                return (
                  <Col key={group.key}>
                    <Card>
                      <Card.Body>
                        <Card.Title className="px-1">
                          {goingUserNames.length > 0
                            ? goingUserNames.join(', ')
                            : firstTrip.name || firstTrip.usersList || 'Trip'}
                        </Card.Title>
                        <Card.Text className="px-1">
                          <strong>Passageiros:</strong>
                          {goingUserNames.length > 0
                            ? goingUserNames.join(', ')
                            : firstTrip.name || firstTrip.usersList || 'N/A'}
                          <br />
                          <strong>Horário:</strong> {formatTime(group.start)} -{' '}
                          {formatTime(group.end)}
                          <br />
                          <strong>Destino:</strong>
                          {locations.join(', ') || 'N/A'}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ========== TRIP LIST (near future + future) ========== */}
      {showTripListCard && (
        <Card className="card-container-trip-management mb-3">
          <Card.Header className="card-container-header">
            <h5 className="mb-0">Viagens de Hoje</h5>
          </Card.Header>
          <Card.Body>
            <Row xs={1} md={2} lg={3} className="g-3">
              {displayableTripGroups.map(group => {
                const goingUserIds = getGroupPassengers(group.trips);
                const participantTrip = group.trips.find(isUserParticipant);
                const firstTrip = participantTrip || group.trips[0];
                const tripHasPassed = dayjs(group.end).isBefore(dayjs());
                const locations = getGroupLocations(group.trips);

                return (
                  <Col key={group.key}>
                    <Card
                      className={`card-container-trip-management-item${
                        tripHasPassed
                          ? ' card-container-trip-management-item-disabled'
                          : !participantTrip
                            ? ' card-container-trip-management-item-not-allowed'
                            : ''
                      }${
                        group.trips.some(
                          t => isNearFutureTrip(t) || shouldBeUnderway(t)
                        )
                          ? ' border-warning'
                          : ''
                      }`}
                      onClick={() => {
                        if (
                          participantTrip &&
                          !isTripEnded(firstTrip) &&
                          !tripHasPassed
                        ) {
                          setSelectedBaseTrip(participantTrip);
                        }
                      }}
                    >
                      <Card.Body>
                        <Card.Title className="px-1">
                          Passageiros:{' '}
                          {goingUserIds.length > 0
                            ? goingUserIds.join(', ')
                            : firstTrip.name || firstTrip.usersList || 'Trip'}
                        </Card.Title>
                        <Card.Text className="px-1">
                          <strong>Horário:</strong> {formatTime(group.start)} -{' '}
                          {formatTime(group.end)}
                          <br />
                          <strong>Destino:</strong>{' '}
                          {locations.join(', ') || 'N/A'}
                        </Card.Text>
                        {participantTrip && !tripHasPassed && (
                          <Button
                            variant="primary"
                            className="w-100"
                            disabled={isTripEnded(participantTrip)}
                            onClick={event => {
                              event.stopPropagation();
                              if (!isTripEnded(participantTrip)) {
                                setSelectedBaseTrip(participantTrip);
                              }
                            }}
                          >
                            Selecionar viagem
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ========== QUICK TRIP ========== */}
      {canShowQuickTrip && (
        <Card>
          <Card.Body className="card-container-trip-management-quicktrip">
            <h5 className="card-container-title">Saída rápida</h5>
            <p className="card-text">
              {displayableTrips.length > 0
                ? 'Este carro não tem viagens ativas no momento. Iniciar uma saída rápida?'
                : 'Este carro não tem viagens hoje. Iniciar uma saída rápida?'}
            </p>

            {displayableTrips.length > 0 && nextFutureTrip && (
              <p className="text-muted mb-3">
                Duração máxima: {calculateMaxQuickTripDuration(nextFutureTrip)}{' '}
                minutos
              </p>
            )}

            <Button variant="primary" onClick={handleQuickTrip}>
              Iniciar saída rápida
            </Button>
          </Card.Body>
          <QuickTripModal
            show={showQuickTripModal}
            onHide={() => setShowQuickTripModal(false)}
            guidCarro={guidCarro || ''}
            matricula={specificCarTrips[0]?.carPlate}
            maxDuration={
              displayableTrips.length > 0 && nextFutureTrip
                ? calculateMaxQuickTripDuration(nextFutureTrip)
                : 60
            }
            onSuccess={async () => {
              await getSpecificCarTrips(guidCarro || '');
            }}
          />
        </Card>
      )}

      <Modal
        show={pendingStartTripId !== null}
        onHide={() => setPendingStartTripId(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Viagem em curso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            Uma viagem neste carro ainda está em curso, tem a certeza que quer
            iniciar a viagem?
          </p>
          <p className="small text-muted mb-0">
            Irá finalizar a viagem anterior e tornar-se responsável pelo carro.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setPendingStartTripId(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (pendingStartTripId !== null) {
                void completeStartTrip(pendingStartTripId);
              }
            }}
          >
            Iniciar viagem
          </Button>
        </Modal.Footer>
      </Modal>

      <ObservationModal
        show={showObservationModal}
        onHide={() => setShowObservationModal(false)}
        onSuccess={() => setShowObservationModal(false)}
        matricula={specificCarTrips[0]?.carPlate || ''}
        guidCarro={guidCarro || ''}
        tripId={selectedTripForObservation || 0}
      />
    </div>
  );
};

export default index;
